import * as LocalAuthentication from 'expo-local-authentication';
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { AppState, AppStateStatus, Platform } from 'react-native';

import { StackActions } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';

import biometrics from '../biometrics/biometrics';
import { useLogout } from '../hooks/user/useLogout';
import { useUserContext } from './UserContext';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface LocalAuthenticationContextProps {}

export const LocalAuthenticationContext =
  createContext<LocalAuthenticationContextProps>(null!);

export const useLocalAuthenticationContext = () => {
  const context = useContext(LocalAuthenticationContext);
  if (!context) {
    throw new Error(
      'useLocalAuthenticationContext must be used within the context of the LocalAuthenticationProvider component'
    );
  }
  return context;
};

export const LocalAuthenticationProvider: React.FC<
  PropsWithChildren & {
    navigation: StackNavigationProp<any>;
  }
> = ({ navigation, children }) => {
  const appState = useRef(AppState.currentState);
  const { user } = useUserContext();
  const { isLoggedOut, logout } = useLogout();

  useEffect(() => {
    if (Platform.OS === 'web') return;
    if (!user) return;

    const handleAppStateChange = async (nextAppState: AppStateStatus) => {
      if (appState.current.match(/background/) && nextAppState === 'active') {
        if (Platform.OS === 'ios') {
          await biometrics.setUserAuthenticated(false);
        } else if (Platform.OS === 'android') {
          await biometrics.setIsAuthenticating(true);
          await new Promise(resolve => setTimeout(resolve, 1000));
          await biometrics.setIsAuthenticating(false);
        }
        // console.log('handleBiometricAuth', nextAppState);
        if (!user) return;
        const routeName = await navigation?.getState()?.routes[0]?.name;
        if (routeName === 'IntroNavigator') return;
        const userAlreadyAuthenticated =
          await biometrics.getUserAuthenticated();
        // console.log('userAlreadyAuthenticated', userAlreadyAuthenticated);
        if (userAlreadyAuthenticated) return;
        const hasEnrolledBiometric =
          await LocalAuthentication.isEnrolledAsync();
        // console.log('hasEnrolledBiometric', hasEnrolledBiometric);
        if (!hasEnrolledBiometric) return;
        const biometricLoginEnabled = await biometrics.getBiometricLogin();
        // console.log('biometricLoginEnabled', biometricLoginEnabled);
        if (!biometricLoginEnabled) {
          await biometrics.setUserAuthenticated(true);
          return;
        }
        // AppState.removeEventListener('change', handleAppStateChange);
        const biometricAuth = await LocalAuthentication.authenticateAsync({
          promptMessage: 'Login with Biometrics',
          cancelLabel: 'Cancel',
          disableDeviceFallback: false
        });
        // console.log('biometricAuth', biometricAuth);
        if (!biometricAuth.success && biometricAuth.error === 'user_cancel') {
          await logout({ reason: 'biometric-cancel' });
          navigation?.dispatch(
            StackActions.replace('IntroNavigator', {
              screen: 'LoginScreen'
            })
          );
        } else if (biometricAuth.success) {
          biometrics.setUserAuthenticated(true);
        }
      } else if (
        appState.current.match(/active/) &&
        nextAppState === 'background'
      ) {
        if (Platform.OS === 'android') {
          const isAuthenticating = await biometrics.getIsAuthenticating();
          if (isAuthenticating === null) {
            await biometrics.setUserAuthenticated(false);
          }
        }
      }

      appState.current = nextAppState;
    };

    const subscription = AppState.addEventListener(
      'change',
      handleAppStateChange
    );
    return () => {
      subscription.remove();
    };
  }, [logout, navigation, user]);

  return (
    <LocalAuthenticationContext.Provider value={{}}>
      {children}
    </LocalAuthenticationContext.Provider>
  );
};
